import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Paper,
  TextField,
  Menu,
  MenuItem,
  InputAdornment,
  TableSortLabel,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import Cookies from 'js-cookie';
import { ADMIN_COOKIE_KEY } from '../../hooks/useAdmin';

interface MediaFile {
  id: string;
  filename: string;
  url: string;
  createdAt: string;
  size: number;
}

// Тип сортировки и направление
type SortField = 'filename' | 'createdAt' | 'size';
type SortDirection = 'asc' | 'desc';

// Тип фильтра файлов
type FileType = 'all' | 'image' | 'document' | 'other';

const Media: React.FC = () => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [fileToRename, setFileToRename] = useState<MediaFile | null>(null);
  const [newFilename, setNewFilename] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  // Состояния для сортировки и фильтрации
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<FileType>('all');
  // Состояния для предварительного просмотра
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const dropZoneRef = React.useRef<HTMLDivElement>(null);
  
  // Состояние для мультизагрузки
  const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
  
  // Добавляем состояние для обновления списка файлов
  const [refreshing, setRefreshing] = useState(false);
  
  // Состояние для статистики
  const [stats, setStats] = useState({
    total: 0,
    images: 0,
    documents: 0,
    other: 0,
    totalSize: 0
  });
  
  // Добавляем состояние для отслеживания перетаскивания над модулем
  const [isDraggingOverModule, setIsDraggingOverModule] = useState(false);
  
  // Добавляем ref для всего модуля
  const moduleRef = React.useRef<HTMLDivElement>(null);
  
  const fetchMediaFiles = async () => {
    try {
      setLoading(true);
      setRefreshing(true);
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      const response = await fetch('https://guide.zerdegroup.kz/api/media', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error('Ошибка при загрузке медиафайлов');
      const data = await response.json();
      setFiles(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке медиафайлов');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchMediaFiles();
    
    // Добавляем глобальный обработчик вставки
    const handleGlobalPaste = async (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          event.preventDefault();
          
          const file = item.getAsFile();
          if (file) {
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `screenshot-${timestamp}.jpeg`;
            const newFile = new File([file], filename, { type: 'image/jpeg' });
            
            setSelectedFile(newFile);
            
            // Создаем и устанавливаем превью перед загрузкой
            const objectUrl = URL.createObjectURL(newFile);
            setPreviewUrl(objectUrl);
            
            await handleUpload(newFile);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);

    // Обработчик для предотвращения стандартного поведения браузера при drop
    const preventDefaultBehavior = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Добавляем глобальные обработчики для предотвращения стандартного поведения
    window.addEventListener('dragover', preventDefaultBehavior);
    window.addEventListener('drop', preventDefaultBehavior);
    
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
      window.removeEventListener('dragover', preventDefaultBehavior);
      window.removeEventListener('drop', preventDefaultBehavior);
    };
  }, []);

  // Очистка URL объектов при размонтировании или при изменении превью
  useEffect(() => {
    return () => {
      // Очищаем предыдущий URL объект при размонтировании или изменении
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      // Обрабатываем загрузку одного или нескольких файлов
      const filesArray = Array.from(event.target.files);
      setMultipleFiles(filesArray);
      
      // Для предпросмотра показываем только первый файл
      const firstFile = filesArray[0];
      setSelectedFile(firstFile);
      
      // Создаем URL для предпросмотра первого файла
      if (firstFile.type.startsWith('image/')) {
        // Очищаем предыдущий URL объект если он есть
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        const objectUrl = URL.createObjectURL(firstFile);
        setPreviewUrl(objectUrl);
      } else {
        setPreviewUrl(null);
      }
      
      // Загружаем каждый файл по очереди
      for (const file of filesArray) {
        await handleUpload(file);
      }
      
      setMultipleFiles([]);
    }
  };

  const handleUpload = async (file: File) => {
    const formData = new FormData();
    
    // Обрабатываем оригинальное имя файла
    const originalName = file.name;
    const extension = originalName.split('.').pop() || '';
    const nameWithoutExt = originalName.slice(0, originalName.lastIndexOf('.'));
    
    // Стандартная транслитерация вместо специальных символов
    const translitMap: { [key: string]: string } = {
      'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
      'е': 'e', 'ё': 'yo', 'ж': 'zh', 'з': 'z', 'и': 'i',
      'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
      'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
      'у': 'u', 'ф': 'f', 'х': 'kh', 'ц': 'ts', 'ч': 'ch',
      'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '',
      'э': 'e', 'ю': 'yu', 'я': 'ya',
      // Добавляем заглавные буквы
      'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D',
      'Е': 'E', 'Ё': 'YO', 'Ж': 'ZH', 'З': 'Z', 'И': 'I',
      'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M', 'Н': 'N',
      'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T',
      'У': 'U', 'Ф': 'F', 'Х': 'KH', 'Ц': 'TS', 'Ч': 'CH',
      'Ш': 'SH', 'Щ': 'SCH', 'Ъ': '', 'Ы': 'Y', 'Ь': '',
      'Э': 'E', 'Ю': 'YU', 'Я': 'YA'
    };
    
    const safeFileName = nameWithoutExt
      .split('')
      .map(char => translitMap[char] || char)
      .join('')
      .replace(/[^a-zA-Z0-9-_.]/g, '-') // оставляем только безопасные символы
      .replace(/-+/g, '-') // заменяем множественные дефисы на один
      .trim();
    
    // Добавляем расширение обратно
    const finalFileName = `${safeFileName}.${extension.toLowerCase()}`;
    
    // Создаем новый файл с безопасным именем
    const renamedFile = new File([file], finalFileName, { type: file.type });
    
    formData.append('file', renamedFile);
    formData.append('originalName', finalFileName);
    const token = Cookies.get(ADMIN_COOKIE_KEY);

    try {
      setUploading(true);
      setUploadProgress(0);
      setError(null);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', async () => {
        if (xhr.status === 200) {
          await fetchMediaFiles();
          setSelectedFile(null);
          setUploadSuccess(true);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          setTimeout(() => {
            setUploadSuccess(false);
          }, 3000);
        } else {
          setError('Ошибка при загрузке файла');
        }
        setUploading(false);
        setUploadProgress(0);
      });

      xhr.addEventListener('error', () => {
        setError('Ошибка при загрузке файла');
        setUploading(false);
        setUploadProgress(0);
      });

      xhr.open('POST', 'https://guide.zerdegroup.kz/api/media/upload');
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при загрузке файла');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id: string) => {
    setConfirmDelete(null);
    const token = Cookies.get(ADMIN_COOKIE_KEY);
    
    try {
      const response = await fetch(`https://guide.zerdegroup.kz/api/media/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка при удалении файла');
      }

      setFiles(files.filter(file => file.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Произошла ошибка при удалении файла');
    }
  };

  const copyToClipboard = (text: string, fileId: string) => {
    if (navigator.clipboard && window.isSecureContext) {
      // Для современных браузеров
      navigator.clipboard.writeText(text).then(
        () => {
          setCopySuccess(fileId);
          setTimeout(() => setCopySuccess(null), 2000);
        },
        (err) => {
          console.error('Не удалось скопировать: ', err);
          fallbackCopyToClipboard(text, fileId);
        }
      );
    } else {
      // Запасной вариант для старых браузеров
      fallbackCopyToClipboard(text, fileId);
    }
  };

  const fallbackCopyToClipboard = (text: string, fileId: string) => {
    try {
      // Создаем временный элемент input
      const textArea = document.createElement('textarea');
      textArea.value = text;
      
      // Предотвращаем прокрутку до элемента
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setCopySuccess(fileId);
        setTimeout(() => setCopySuccess(null), 2000);
      } catch (err) {
        console.error('Не удалось скопировать текст: ', err);
      }
      
      document.body.removeChild(textArea);
    } catch (err) {
      console.error('Не удалось скопировать текст: ', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRename = async () => {
    if (!fileToRename || !newFilename) return;

    try {
      setRenameError(null);
      const token = Cookies.get(ADMIN_COOKIE_KEY);
      
      // Получаем расширение оригинального файла
      const originalExt = fileToRename.filename.split('.').pop()?.toLowerCase() || '';
      
      // Собираем итоговое имя
      const finalNewName = `${newFilename}.${originalExt}`;
      
      const response = await fetch(`https://guide.zerdegroup.kz/api/media/${fileToRename.id}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ filename: finalNewName })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Ошибка при переименовании файла');
      }

      // Обновляем список файлов
      await fetchMediaFiles();
      setRenameDialogOpen(false);
      setFileToRename(null);
      setNewFilename('');
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Произошла ошибка при переименовании файла');
    }
  };

  const openRenameDialog = (file: MediaFile) => {
    setFileToRename(file);
    // Устанавливаем имя файла без расширения
    const nameWithoutExt = file.filename.split('.').slice(0, -1).join('.');
    setNewFilename(nameWithoutExt);
    setRenameError(null);
    setRenameDialogOpen(true);
  };

  // Вспомогательная функция для проверки, является ли файл изображением
  const isImageFile = (filename: string) => {
    return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(filename);
  };

  // Функция для определения типа файла
  const getFileType = (filename: string): FileType => {
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension)) {
      return 'image';
    } else if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(extension)) {
      return 'document';
    }
    return 'other';
  };

  // Обрабатываем фильтрацию и сортировку при изменении данных
  useEffect(() => {
    if (!files.length) {
      setFilteredFiles([]);
      return;
    }

    // Шаг 1: Применяем фильтр по типу файла
    let result = [...files];
    if (fileTypeFilter !== 'all') {
      result = result.filter(file => getFileType(file.filename) === fileTypeFilter);
    }

    // Шаг 2: Применяем поиск по имени файла
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(file => file.filename.toLowerCase().includes(query));
    }

    // Шаг 3: Сортировка
    result.sort((a, b) => {
      let comparison = 0;
      
      if (sortField === 'filename') {
        comparison = a.filename.localeCompare(b.filename);
      } else if (sortField === 'size') {
        comparison = a.size - b.size;
      } else if (sortField === 'createdAt') {
        comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    setFilteredFiles(result);
  }, [files, sortField, sortDirection, searchQuery, fileTypeFilter]);

  // Обработчик изменения сортировки
  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Функция для получения иконки файла
  const getFileIcon = (filename: string) => {
    const fileType = getFileType(filename);
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    
    if (fileType === 'image') {
      return <ImageIcon fontSize="small" />;
    } else if (extension === 'pdf') {
      return <PictureAsPdfIcon fontSize="small" />;
    } else if (['doc', 'docx', 'txt'].includes(extension)) {
      return <TextSnippetIcon fontSize="small" />;
    } else {
      return <AttachFileIcon fontSize="small" />;
    }
  };

  // Модифицируем функцию открытия просмотрщика изображений
  const openImageViewer = (url: string) => {
    // Добавляем домен API для файлов из таблицы, если URL начинается с /
    const fullUrl = url.startsWith('/') ? `https://guide.zerdegroup.kz/api${url}` : url;
    setPreviewImageUrl(fullUrl);
    setImageViewerOpen(true);
  };

  // Модифицируем функцию для очистки формы загрузки
  const resetUploadForm = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setMultipleFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Обновление статистики при изменении файлов
  useEffect(() => {
    if (!files.length) {
      setStats({
        total: 0,
        images: 0,
        documents: 0,
        other: 0,
        totalSize: 0
      });
      return;
    }

    const newStats = {
      total: files.length,
      images: 0,
      documents: 0,
      other: 0,
      totalSize: 0
    };

    files.forEach(file => {
      const fileType = getFileType(file.filename);
      if (fileType === 'image') {
        newStats.images++;
      } else if (fileType === 'document') {
        newStats.documents++;
      } else {
        newStats.other++;
      }
      newStats.totalSize += file.size;
    });

    setStats(newStats);
  }, [files]);

  // Функция для сброса всех фильтров
  const resetFilters = () => {
    setSortField('createdAt');
    setSortDirection('desc');
    setSearchQuery('');
    setFileTypeFilter('all');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ p: 3 }} 
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOverModule(true);
        // Подсвечиваем dropZone при перетаскивании над модулем
        if (dropZoneRef.current) {
          dropZoneRef.current.classList.add('drag-over');
        }
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        // Проверяем, действительно ли курсор вышел за пределы модуля
        const rect = e.currentTarget.getBoundingClientRect();
        const { clientX, clientY } = e;
        if (
          clientX <= rect.left ||
          clientX >= rect.right ||
          clientY <= rect.top ||
          clientY >= rect.bottom
        ) {
          setIsDraggingOverModule(false);
          // Убираем подсветку dropZone
          if (dropZoneRef.current) {
            dropZoneRef.current.classList.remove('drag-over');
          }
        }
      }}
      onDrop={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOverModule(false);
        
        // Убираем подсветку dropZone
        if (dropZoneRef.current) {
          dropZoneRef.current.classList.remove('drag-over');
        }
        
        if (uploading) return; // Предотвращаем несколько одновременных загрузок
        
        if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
          const filesArray = Array.from(e.dataTransfer.files);
          setMultipleFiles(filesArray);
          
          // Для предпросмотра показываем только первый файл
          const firstFile = filesArray[0];
          setSelectedFile(firstFile);
          
          // Создаем URL для предпросмотра первого файла
          if (firstFile.type.startsWith('image/')) {
            const objectUrl = URL.createObjectURL(firstFile);
            setPreviewUrl(objectUrl);
          } else {
            setPreviewUrl(null);
          }
          
          // Загружаем каждый файл по очереди
          for (const file of filesArray) {
            await handleUpload(file);
          }
        }
      }}
      ref={moduleRef}
    >
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          position: 'relative',
          transition: 'all 0.3s ease',
          ...(isDraggingOverModule && {
            boxShadow: theme => `0 0 0 2px ${theme.palette.primary.main}`,
          })
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Управление медиафайлами
          </Typography>
          <Tooltip title="Обновить список файлов">
            <IconButton onClick={fetchMediaFiles} disabled={refreshing}>
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ 
          mb: 3, 
          p: 2, 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'background.paper',
          transition: 'all 0.3s ease',
          ...(isDraggingOverModule && {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          })
        }}>
          <input
            type="file"
            id="media-upload"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            ref={fileInputRef}
            disabled={uploading}
            multiple // Разрешаем множественный выбор файлов
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" // Ограничиваем типы файлов
          />
          <Box 
            ref={dropZoneRef}
            tabIndex={0}
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              p: 2,
              border: '2px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              backgroundColor: 'background.default',
              cursor: 'pointer',
              outline: 'none',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&:focus': {
                borderColor: 'primary.dark',
                backgroundColor: 'action.hover',
              },
              '&.drag-over': {
                backgroundColor: 'primary.light',
                borderColor: 'primary.dark',
                opacity: 0.7,
              }
            }}
            onClick={() => !uploading && fileInputRef.current?.click()}
          >
            <CloudUploadIcon color={uploading ? "disabled" : "primary"} />
            <Typography variant="body1" color={uploading ? "text.secondary" : "primary"}>
              {uploading 
                ? "Идет загрузка файла..." 
                : isDraggingOverModule 
                  ? "Отпустите файл для загрузки" 
                  : "Перетащите файлы сюда или вставьте изображение из буфера обмена (Ctrl+V)"}
            </Typography>
          </Box>
          
          {selectedFile && (
            <Box sx={{ flex: 1, mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                alignItems: 'flex-start', 
                p: 2,
                backgroundColor: 'action.hover',
                borderRadius: 1
              }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {multipleFiles.length > 1 
                        ? `Выбрано файлов: ${multipleFiles.length}` 
                        : `${selectedFile.name} (${formatFileSize(selectedFile.size)})`}
                    </Typography>
                    {!uploading && (
                      <IconButton size="small" onClick={resetUploadForm}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {uploading && (
                    <Box sx={{ width: '100%' }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {Math.round(uploadProgress)}%
                        {multipleFiles.length > 1 && ` (файл ${multipleFiles.findIndex(f => f.name === selectedFile.name) + 1} из ${multipleFiles.length})`}
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {previewUrl && (
                  <Box 
                    sx={{ 
                      width: 80,
                      height: 80,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      position: 'relative',
                      '&:hover': {
                        cursor: 'zoom-in',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0,0,0,0.3)',
                        }
                      }
                    }}
                    onClick={() => openImageViewer(previewUrl)}
                  >
                    <img 
                      src={previewUrl} 
                      alt="Preview"
                      style={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          )}
          
          {uploadSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Файл успешно загружен
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* Статистика и управление фильтрами */}
        <Box sx={{ 
          mb: 3, 
          p: 2, 
          backgroundColor: 'background.default',
          borderRadius: 1,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Всего файлов
              </Typography>
              <Typography variant="h6">
                {stats.total}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Изображения
              </Typography>
              <Typography variant="h6">
                {stats.images}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Документы
              </Typography>
              <Typography variant="h6">
                {stats.documents}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Прочее
              </Typography>
              <Typography variant="h6">
                {stats.other}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Общий размер
              </Typography>
              <Typography variant="h6">
                {formatFileSize(stats.totalSize)}
              </Typography>
            </Box>
          </Box>
          
          {(searchQuery || fileTypeFilter !== 'all' || sortField !== 'createdAt' || sortDirection !== 'desc') && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={resetFilters}
            >
              Сбросить фильтры
            </Button>
          )}
        </Box>

        {/* Добавляем панель фильтрации и поиска */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Поиск по имени файла"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Тип файла</InputLabel>
            <Select
              value={fileTypeFilter}
              label="Тип файла"
              onChange={(e: SelectChangeEvent<FileType>) => 
                setFileTypeFilter(e.target.value as FileType)
              }
            >
              <MenuItem value="all">Все файлы</MenuItem>
              <MenuItem value="image">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon fontSize="small" />
                  <span>Изображения ({stats.images})</span>
                </Box>
              </MenuItem>
              <MenuItem value="document">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextSnippetIcon fontSize="small" />
                  <span>Документы ({stats.documents})</span>
                </Box>
              </MenuItem>
              <MenuItem value="other">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachFileIcon fontSize="small" />
                  <span>Прочее ({stats.other})</span>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'filename'}
                    direction={sortField === 'filename' ? sortDirection : 'asc'}
                    onClick={() => handleSortChange('filename')}
                  >
                    Имя файла
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'size'}
                    direction={sortField === 'size' ? sortDirection : 'asc'}
                    onClick={() => handleSortChange('size')}
                  >
                    Размер
                  </TableSortLabel>
                </TableCell>
                <TableCell>Ссылка</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'createdAt'}
                    direction={sortField === 'createdAt' ? sortDirection : 'asc'}
                    onClick={() => handleSortChange('createdAt')}
                  >
                    Дата загрузки
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFiles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    {files.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Нет загруженных файлов
                        </Typography>
                        <Button 
                          variant="contained" 
                          sx={{ mt: 2 }}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Загрузить файлы
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <FilterListIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="body1" sx={{ mt: 1 }}>
                          Нет файлов, соответствующих критериям поиска
                        </Typography>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          sx={{ mt: 2 }}
                          onClick={resetFilters}
                        >
                          Сбросить фильтры
                        </Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredFiles.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isImageFile(file.filename) ? (
                          <Box 
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                            onClick={() => openImageViewer(file.url)}
                          >
                            <img
                              src={`https://guide.zerdegroup.kz/api${file.url}`}
                              alt={file.filename}
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 6,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                background: '#eee',
                                marginRight: 8
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ mr: 1 }}>{getFileIcon(file.filename)}</Box>
                        )}
                        {file.filename}
                        <IconButton
                          size="small"
                          onClick={() => openRenameDialog(file)}
                          title="Переименовать файл"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{formatFileSize(file.size)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link 
                          href={`https://guide.zerdegroup.kz/api${file.url}`}
                          target="_blank"
                          sx={{ 
                            maxWidth: '300px', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            display: 'block'
                          }}
                        >
                          {file.url}
                        </Link>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, position: 'relative' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => copyToClipboard(`https://guide.zerdegroup.kz/api${file.url}`, file.id)}
                            title="Копировать ссылку"
                            color={copySuccess === file.id ? "success" : "default"}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          {copySuccess === file.id && (
                            <Typography 
                              variant="caption" 
                              color="success.main"
                              sx={{ 
                                position: 'absolute',
                                left: '100%',
                                whiteSpace: 'nowrap',
                                backgroundColor: 'background.paper',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                boxShadow: 1,
                                ml: 1
                              }}
                            >
                              Скопировано!
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {new Date(file.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => setConfirmDelete(file.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Добавляем информацию о количестве отображаемых файлов */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, color: 'text.secondary' }}>
          <Typography variant="body2">
            {filteredFiles.length === files.length 
              ? `Показано ${files.length} файлов` 
              : `Показано ${filteredFiles.length} из ${files.length} файлов`}
          </Typography>
        </Box>

        {/* Улучшенный просмотрщик изображений с управлением */}
        <Dialog
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          maxWidth={false}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
              maxWidth: '100%',
              maxHeight: '100%',
            }
          }}
        >
          <DialogContent sx={{ 
            p: 0, 
            position: 'relative', 
            bgcolor: 'background.paper',
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '60vh',
                maxHeight: '85vh',
                position: 'relative'
              }}
            >
              <img 
                src={previewImageUrl} 
                alt="Image preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                }}
                onLoad={(e) => {
                  // Устанавливаем размер диалога в соответствии с размерами изображения
                  const img = e.target as HTMLImageElement;
                  const aspectRatio = img.naturalWidth / img.naturalHeight;
                  const maxWidth = Math.min(window.innerWidth * 0.9, img.naturalWidth);
                  const maxHeight = Math.min(window.innerHeight * 0.9, img.naturalHeight);
                  
                  const dialogContent = (e.target as HTMLImageElement).parentElement?.parentElement;
                  if (dialogContent) {
                    dialogContent.style.width = `${maxWidth}px`;
                    dialogContent.style.maxWidth = `${maxWidth}px`;
                  }
                }}
              />
            </Box>
            <IconButton
              onClick={() => setImageViewerOpen(false)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.5)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogContent>
        </Dialog>

        {/* Диалог переименования */}
        <Dialog
          open={renameDialogOpen}
          onClose={() => {
            setRenameDialogOpen(false);
            setFileToRename(null);
            setNewFilename('');
            setRenameError(null);
          }}
        >
          <DialogTitle>Переименовать файл</DialogTitle>
          <DialogContent>
            {renameError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {renameError}
              </Alert>
            )}
            <Box
              component="form"
              onSubmit={e => {
                e.preventDefault();
                if (!newFilename || newFilename === fileToRename?.filename.split('.').slice(0, -1).join('.')) return;
                handleRename();
              }}
            >
              <TextField
                autoFocus
                margin="dense"
                label="Новое имя файла"
                fullWidth
                value={newFilename}
                onChange={(e) => {
                  // Оставляем только имя до первой точки
                  const value = e.target.value;
                  const dotIndex = value.indexOf('.');
                  if (dotIndex !== -1) {
                    setNewFilename(value.substring(0, dotIndex));
                  } else {
                    setNewFilename(value);
                  }
                  setRenameError(null);
                }}
                error={!!renameError}
                helperText={renameError || `Расширение файла (.${fileToRename?.filename.split('.').pop()}) будет добавлено автоматически`}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setRenameDialogOpen(false);
                setFileToRename(null);
                setNewFilename('');
                setRenameError(null);
              }}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleRename}
              variant="contained"
              disabled={!newFilename || newFilename === fileToRename?.filename.split('.').slice(0, -1).join('.')}
            >
              Переименовать
            </Button>
          </DialogActions>
        </Dialog>

        {/* Диалог подтверждения удаления */}
        <Dialog
          open={confirmDelete !== null}
          onClose={() => setConfirmDelete(null)}
        >
          <DialogTitle>Подтверждение удаления</DialogTitle>
          <DialogContent>
            <Typography>
              Вы уверены, что хотите удалить этот файл? Это действие нельзя отменить.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)}>Отмена</Button>
            <Button 
              onClick={() => confirmDelete !== null && handleDelete(confirmDelete)} 
              color="error"
              variant="contained"
            >
              Удалить
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default Media;